import * as React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Client } from '../../models';
import moment from 'moment';
import Storage from '@aws-amplify/storage';
import { inject, observer } from 'mobx-react';
import { UserProfileUIStore } from '../../stores/user-profile-ui-store';

const logo = require('../../assets/imgs/logo.png'); // fallback asset (kept for getLogoUrl)
const html2pdf = require('html2pdf.js');

Storage.configure({
  AWSS3: {
    bucket: 'labsmarts-next',
    region: 'us-east-1',
  },
});

type Props = {
  show: boolean;
  title: string;
  client: Client;
  collectionDate: Date | string;
  elementId: string;
  onHide: () => void;
  userProfileUIStore?: UserProfileUIStore;
  preloadedLogoDataUrl?: string | null; // Optional: pre-loaded logo data URL from parent
};

type State = {
  show: boolean;
  logoDataUrl: string | null; // preloaded header logo (custom or default)
  footerBrandDataUrl: string | null; // preloaded footer brand logo
  isPdfReady: boolean; // indicates if all PDF assets are prepared
  preparedPdf: any | null; // Pre-generated PDF object ready for save
};

@inject('userProfileUIStore')
@observer
export class PDFModal extends React.Component<Props, {}> {
  state: State = {
    show: this.props.show,
    logoDataUrl: null,
    footerBrandDataUrl: null,
    isPdfReady: false,
    preparedPdf: null,
  };

  componentWillReceiveProps(nextProps: Props) {
    this.setState({ show: nextProps.show });
  }

  // Prepare all PDF assets in advance
  private preparePdfAssets = async (): Promise<void> => {
    try {
      // Use preloaded logo if provided, otherwise resolve it
      const logoDataUrl =
        this.props.preloadedLogoDataUrl ||
        (await this.resolveHeaderLogoDataUrl());
      const footerBrandDataUrl = await this.resolveFooterBrandDataUrl();

      console.log(
        '[PDFModal] logoDataUrl:',
        logoDataUrl ? `${logoDataUrl.substring(0, 50)}...` : 'null',
      );
      console.log(
        '[PDFModal] preloadedLogoDataUrl:',
        this.props.preloadedLogoDataUrl
          ? `${this.props.preloadedLogoDataUrl.substring(0, 50)}...`
          : 'null',
      );

      this.setState({
        logoDataUrl,
        footerBrandDataUrl,
      });

      // Update the logo in DOM so html2canvas picks up a data URL (works for all users)
      const img = document.getElementById('logo') as HTMLImageElement | null;
      if (img && logoDataUrl) {
        img.src = logoDataUrl;
      }

      // Now prepare the actual PDF
      await this.preparePdf(logoDataUrl, footerBrandDataUrl);
    } catch (error) {
      console.error('Error preparing PDF assets:', error);
      // Set ready to true anyway so export can still work with fallback
      this.setState({ isPdfReady: true });
    }
  };

  // Prepare the actual PDF object in advance
  private preparePdf = async (
    logoDataUrl: string,
    footerBrandDataUrl: string,
  ): Promise<void> => {
    try {
      const { client, collectionDate, elementId } = this.props;

      const firstInitialOfFirstName = client.firstName.charAt(0);
      const lastName = client.lastName;
      const formattedCollectionDate =
        moment(collectionDate).format('YYYY-MM-DD');
      const filename = `${lastName}_${firstInitialOfFirstName}-LabSmarts_Bloodwork_Comparison_${formattedCollectionDate}.pdf`;

      const comparisonPanel = document.getElementById(elementId);
      const pdfExportButton = document.getElementById('pdf-export-button');
      if (pdfExportButton) pdfExportButton.style.opacity = '0';

      if (!comparisonPanel) {
        console.error('PDF comparison panel not found');
        return;
      }

      const baseOpt = {
        margin: [3, 5, 10, 5],
        filename,
        image: { type: 'jpeg', quality: 1 },
        // See bloodwork-analysis-pdf.tsx: html2pdf's link annotations fragment
        // per text-wrap rect and land on mis-computed pages, so they never
        // worked, and they read to AV heuristics as a scam-document link mat.
        enableLinks: false,
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          scrollX: 0,
          scrollY: 0,
          onclone: (doc: Document) => {
            const img = doc.getElementById('logo') as HTMLImageElement | null;
            if (img && logoDataUrl) {
              img.src = logoDataUrl;
            }
          },
        },
        jsPDF: { orientation: 'l' },
        pagebreak: { mode: ['css', 'legacy'], avoid: '.disclaimer-panel' },
      };

      // Generate PDF and add footer
      const preparedPdf = await (html2pdf() as any)
        .set(baseOpt)
        .from(comparisonPanel)
        .toPdf()
        .get('pdf')
        .then((pdf: any) => {
          // See bloodwork-analysis-pdf.tsx: an untitled, unattributed PDF whose
          // pages are all rasterized images is what AVG/Avast score as
          // "PDF:MalwareX-gen [Scam]". Keep both export paths named.
          pdf.setProperties({
            title: `LabSmarts Bloodwork Comparison (${formattedCollectionDate})`,
            subject: 'Blood work comparison report',
            author:
              this.props.userProfileUIStore?.currentUserProfile?.preparedBy ||
              'LabSmarts',
            creator: 'LabSmarts',
          });

          // Only add footer if we have the brand data URL
          if (footerBrandDataUrl) {
            // Keep the logo aspect ratio natural (no squishing)
            const props = pdf.getImageProperties(footerBrandDataUrl);
            const naturalRatio = props.width / props.height;

            const footerText = 'Powered by';
            const footerFontSize = 8;

            // Tweak this size to taste; width will follow the real ratio automatically
            const logoHeight = 3.8;
            const logoWidth = logoHeight * naturalRatio;

            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
              pdf.setPage(i);
              const pageWidth = pdf.internal.pageSize.getWidth();
              const pageHeight = pdf.internal.pageSize.getHeight();

              const rightPadding = 8;
              const bottomPadding = 6;
              const gap = 2;
              const baselineY = pageHeight - bottomPadding;

              pdf.setFontSize(footerFontSize);
              const textWidth = pdf.getTextWidth(footerText);
              const blockWidth = textWidth + gap + logoWidth;
              const startX = pageWidth - rightPadding - blockWidth;

              // --- Left Footer Text ---
              const { userProfileUIStore } = this.props;
              const preparedBy =
                userProfileUIStore?.currentUserProfile?.preparedBy;
              const fullName = `${client.firstName} ${client.lastName}`;

              let leftBase = `Prepared for: ${fullName}  •  Most Recent Collection Date: ${moment(
                collectionDate,
              ).format('MM/DD/YYYY')}`;
              if (preparedBy) {
                leftBase += `  •  Prepared by: ${preparedBy}`;
              }

              const leftPadding = 8;
              // Ensure we don't overlap with the right block
              const maxLeftWidth = startX - leftPadding - 10;

              const fit = (s: string) => {
                if (pdf.getTextWidth(s) <= maxLeftWidth) return s;
                let t = s;
                while (t.length > 4 && pdf.getTextWidth(t + '…') > maxLeftWidth)
                  t = t.slice(0, -1);
                return t + '…';
              };
              const leftText = fit(leftBase);

              pdf.text(leftText, leftPadding, baselineY);
              // ------------------------

              pdf.text(footerText, startX, baselineY);
              pdf.addImage(
                footerBrandDataUrl,
                (props.fileType as any) || 'PNG',
                startX + textWidth + gap,
                baselineY - logoHeight + 0.8, // visual baseline alignment
                logoWidth,
                logoHeight,
              );
            }
          }
          return pdf;
        });

      this.setState({
        preparedPdf,
        isPdfReady: true,
      });

      if (pdfExportButton) pdfExportButton.style.opacity = '1';
    } catch (error) {
      console.error('Error preparing PDF:', error);
      // Set ready to true anyway so export can still work with fallback
      this.setState({ isPdfReady: true });
    }
  };

  componentDidUpdate(prevProps: Props) {
    // When the modal opens, prepare all PDF assets
    if (!prevProps.show && this.props.show) {
      this.preparePdfAssets();
    }
  }

  handleClose = () => {
    this.props.onHide();
  };

  getLogoUrl = (): string => {
    const { userProfileUIStore } = this.props;
    const userProfile = userProfileUIStore?.currentUserProfile;
    return userProfile?.hasCustomLogo() ? userProfile.customLogoUrl : logo;
  };

  private toDataUrl = async (url: string): Promise<string> => {
    const res = await fetch(url, { credentials: 'omit' });
    const blob = await res.blob();
    return await new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  };

  // Resolve header logo (custom if available; else bundled default) to a data URL
  private resolveHeaderLogoDataUrl = async (): Promise<string> => {
    const bundled =
      (require('../../assets/imgs/logo.png') as any)?.default ||
      (require('../../assets/imgs/logo.png') as any);

    // No custom logo: use bundled default directly, skip all S3 logic
    if (!this.props.userProfileUIStore?.currentUserProfile?.hasCustomLogo()) {
      return await this.toDataUrl(bundled);
    }

    const raw = this.getLogoUrl() as any;
    const candidate = (raw?.default ?? raw) as string | undefined;

    // 1) If getLogoUrl() gives an http(s) URL, try it directly
    if (candidate && /^https?:\/\//i.test(candidate)) {
      try {
        return await this.toDataUrl(candidate);
      } catch {
        // fall through to S3/default
      }
    }

    // 2) Try S3 (user-logos/<filename from getLogoUrl()>)
    try {
      const key = (candidate || '').toString().split('/').pop();
      if (key) {
        const s3Url = await Storage.get(`user-logos/${key}`);
        return await this.toDataUrl(s3Url as string);
      }
    } catch {
      // fall through to default
    }

    // 3) Fallback: bundled default asset
    return await this.toDataUrl(bundled);
  };

  // Footer brand mark: use bundled default (keeps brand consistent), as a data URL
  private resolveFooterBrandDataUrl = async (): Promise<string> => {
    const brandUrl =
      (require('../../assets/imgs/logo.png') as any)?.default ||
      (require('../../assets/imgs/logo.png') as any);
    return await this.toDataUrl(brandUrl);
  };

  export = async () => {
    const { preparedPdf, isPdfReady } = this.state;
    const { client, collectionDate } = this.props;

    const firstInitialOfFirstName = client.firstName.charAt(0);
    const lastName = client.lastName;
    const formattedCollectionDate = moment(collectionDate).format('YYYY-MM-DD');
    const filename = `${lastName}_${firstInitialOfFirstName}-LabSmarts_Bloodwork_Comparison_${formattedCollectionDate}.pdf`;

    if (preparedPdf && isPdfReady) {
      preparedPdf.save(filename);
    } else {
      // Fallback: prepare PDF on the fly (shouldn't happen normally)
      console.log('PDF not prepared, falling back to on-demand generation...');
      await this.preparePdfAssets();
      if (this.state.preparedPdf) {
        this.state.preparedPdf.save(filename);
      }
    }
  };

  render() {
    if (!this.state.show) return null;
    const { title } = this.props;
    const { isPdfReady } = this.state;

    return (
      <Modal size="xl" show={this.state.show} onHide={this.handleClose}>
        <Modal.Header
          closeButton
          style={{ backgroundColor: '#337ab7', color: '#fff' }}
        >
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-right">
            <Button
              id="pdf-export-button"
              style={{ opacity: isPdfReady ? 1 : 0 }}
              onClick={this.export}
              disabled={!isPdfReady}
            >
              {isPdfReady ? 'Export to PDF' : 'Preparing PDF...'}
            </Button>
          </div>
          {this.props.children}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.handleClose}>Close</Button>
          {isPdfReady && <Button onClick={this.export}>Export to PDF</Button>}
        </Modal.Footer>
      </Modal>
    );
  }
}
